/**
 * Stable test ids for the email-marketing mailing-list surface (the contacts grid,
 * the "create / new mailing list" entry CTAs, and the add-mailing-list wizard).
 *
 * Single source of truth shared by the React source — which renders these as
 * `data-testid` — and the Playwright suite (`tests/support/pages/mailingLists.page.ts`),
 * which queries them via `getByTestId`. Because both sides import the same enum,
 * renaming an id is a compile error in the tests rather than a silent runtime mismatch.
 *
 * Values are kebab-upper so they read clearly in the DOM and in test reports. Test ids
 * are stripped from production builds by the babel strip plugin, so adding one is always safe.
 */
export enum MailingListsTestId {
  // Entry CTAs. NEW/CREATE buttons are `Button`s (forward data-testid); the "Create a new
  // mailing list…" row is a `ClickableDiv` (forwards data-testid).
  NEW_MAILING_LIST_BUTTON = 'MAILING-LIST-NEW-BUTTON',
  CREATE_MAILING_LIST_BUTTON = 'MAILING-LIST-CREATE-BUTTON',
  CREATE_NEW_CTA = 'MAILING-LIST-CREATE-NEW-CTA',
  // A row in the contacts grid (BaseHorizontalGridRow item) — the page object narrows by name.
  MAILING_LIST_GRID_ITEM = 'MAILING-LIST-GRID-ITEM',
  // Add-mailing-list wizard (a shared `Panel`; forwards data-testid to its root).
  ADD_MAILING_LIST_PANEL = 'ADD-MAILING-LIST-PANEL',
  // "Add manually" option card (AddItemCard — extended to forward data-testid to its root <div>).
  ADD_MANUALLY_CARD = 'MAILING-LIST-ADD-MANUALLY-CARD',
  // Mailing-list name input (input-field-v2; forwards inputTestId to its <input>).
  MAILING_LIST_NAME_INPUT = 'MAILING-LIST-NAME-INPUT',
  // Manual-contacts textarea (input-field-v2 via TextInputWithLineNumbers) + the consent Checkbox.
  CONTACTS_INPUT = 'MAILING-LIST-CONTACTS-INPUT',
  CONSENT_CHECKBOX = 'MAILING-LIST-CONSENT-CHECKBOX',
  // Wizard navigation CTAs (NavigationButton — extended to forward data-testid to its wrapper <div>).
  NEXT_UPLOAD_CONTACTS_BUTTON = 'MAILING-LIST-NEXT-UPLOAD-CONTACTS',
  NEXT_SAVE_MAILING_LIST_BUTTON = 'MAILING-LIST-NEXT-SAVE',
  // Share-in-team ToggleSwitch (toggle-switch-v2; forwards data-testid to its inner switch <span>).
  SHARE_IN_TEAM_TOGGLE = 'MAILING-LIST-SHARE-IN-TEAM-TOGGLE',
  // Success-step "Done" CTA (PanelFooterPrimaryDefaultAction extends ButtonProps → forwards data-testid).
  DONE_BUTTON = 'MAILING-LIST-DONE-BUTTON',
}
